/**
 * StageAutomationTables — FTP's read-only tables under the pipeline for the
 * selected stage (admin-stage-details.php), each titled with its count:
 *
 *   Add Tasks     Task, Task Group, Start, Recur — task items of active
 *                 groups the stage starts
 *   Remove Tasks  Task, Task Group — task items of groups the stage stops
 *   Triggers      Trigger — appointment rules that move plans into the stage
 */
import { ADMIN_TD_CLASS, ADMIN_TH_CLASS } from '@/components/admin/AdminList';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';
import type { StageAutomations } from '@/services/stagePipelineAdmin.service';

function AutomationTable({ title, columns, rows }: { title: string; columns: string[]; rows: string[][] }) {
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm" aria-label={title}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c} scope="col" className={cn(ADMIN_TH_CLASS, 'py-2 text-left')}>
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className={cn(ADMIN_TD_CLASS, 'py-2 text-center text-slate-400')}>
                  There are no results
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i}>
                  {r.map((cell, j) => (
                    <td key={j} className={cn(ADMIN_TD_CLASS, 'py-2')}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StageAutomationTables({ automations, className }: { automations: StageAutomations | undefined; className?: string }) {
  return (
    <div data-testid="automation-tables" className={cn('min-w-0 flex-1 space-y-4', className)}>
      {!automations ? (
        <div className="flex justify-center py-8">
          <Spinner size="sm" label="Loading the stage's automations…" />
        </div>
      ) : (
        <>
          <AutomationTable
            title={`Add Tasks ${automations.addTasks.length}`}
            columns={['Task', 'Task Group', 'Start', 'Recur']}
            rows={automations.addTasks.map((t) => [t.taskName, t.taskGroupName, t.start, t.recur])}
          />
          <AutomationTable
            title={`Remove Tasks ${automations.removeTasks.length}`}
            columns={['Task', 'Task Group']}
            rows={automations.removeTasks.map((t) => [t.taskName, t.taskGroupName])}
          />
          <AutomationTable
            title={`Triggers ${automations.triggers.length}`}
            columns={['Trigger']}
            rows={automations.triggers.map((t) => [t.text])}
          />
        </>
      )}
    </div>
  );
}
